import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import Spinner from '../Spinner';
import { ALL_CLEARING_HOUSES, ON_CLEARING_HOUSE_CHANGE } from '../../graphql/clearingHouses';
import ClearingHousesList from './ClearingHousesList';
import Paginator from '../Paginator';
import { parsePaging, parseSorting } from '../../utils/queryString';
import CreateClearingHouseRow from './CreateClearingHouseRow';
import SubscriptionActions from '../../utils/SubscriptionActions';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import { showInfoNotification } from '../../actions/notifications';
import Constants from '../../utils/Constants';
import Error from '../errors/Error';
import useAuthentication from '../../hooks/useAuthentication';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';

export function createNotificationText(t, action, name, code) {
    let notification;
    switch (action) {
        case SubscriptionActions.CLEARING_HOUSE_CREATED:
            notification = t('clearing-houses.notification.clearing-house-create-thirdparty', { name, code });
            break;
        case SubscriptionActions.CLEARING_HOUSE_UPDATED:
            notification = t('clearing-houses.notification.clearing-house-update-thirdparty', { name, code });
            break;
        case SubscriptionActions.CLEARING_HOUSE_DELETED:
            notification = t('clearing-houses.notification.clearing-house-delete-thirdparty', { name, code });
            break;
        default:
            notification = t('clearing-houses.notification.clearing-houses-modified-thirdparty');
    }
    return notification;
}

function ClearingHouses() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'code',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        sortKey,
        sortDirection,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_CLEARING_HOUSES, {
        variables: refetchProps,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    useExtendedSubscription(ON_CLEARING_HOUSE_CHANGE, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'clearingHouseChanged',
        onChangeCallback: ({ action, model: { name, code } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.CLEARING_HOUSE_EDIT])) {
                const notification = createNotificationText(t, action, name, code);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    let clearingHouseProps = null;
    if (loading) {
        result = <Spinner className="clearing-houses-loading" />;
    } else if (error) {
        result = <Error className="clearing-houses-error" error={error} />;
    } else {
        clearingHouseProps = {
            availableTimeZones: data.allClearingHouses.availableTimeZones,
            availableCurrencies: data.allClearingHouses.availableCurrencies,
            minCashLimit: data.allClearingHouses.minCashLimit,
            maxCashLimit: data.allClearingHouses.maxCashLimit,
        };
        result = (
            <Paginator
                totalCount={data.allClearingHouses.clearingHouseList.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <ClearingHousesList
                    clearingHouseProps={clearingHouseProps}
                    clearingHouses={data.allClearingHouses.clearingHouseList.models}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="balance-scale" />&nbsp;{t('title.clearing-houses')}</h4>
            {clearingHouseProps && <CreateClearingHouseRow clearingHouseProps={clearingHouseProps} />}
            {result}
        </>
    );
}

export default ClearingHouses;
